import React, {useMemo, useState} from 'react';
import {Modal, ScrollView, View, TouchableOpacity} from 'react-native';
import styled, {useTheme} from 'styled-components/native';
import Markdown from '@ronradtke/react-native-markdown-display';
import TextGeneric from '../textGeneric';
import Icon from '../icon';
import {copyToClipboard} from '../../utils/helpers';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import {isWeb} from '../../utils/responsive';
import {markdownStyle, markdownRules} from '../../utils/markdownHelpers';

// Markdown-it instance with HTML parsing enabled
const markdownItInstance = require('markdown-it')({
  html: true,
  breaks: true,
  linkify: true,
});

interface DebugModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any[];
}

const Header = styled.View`
  padding: 10px 12px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom-width: 1px;
  border-bottom-color: ${({theme}) => theme.border}33;
  flex-shrink: 0;
`;

const Tabs = styled(ScrollView)`
  padding-horizontal: 8px;
  padding-vertical: 6px;
  border-bottom-width: 1px;
  border-bottom-color: ${({theme}) => theme.border}22;
  flex-shrink: 0;
  max-height: 44px;
  min-height: 44px;
`;

const TabButton = styled.Pressable<{isActive: boolean}>`
  padding-vertical: 5px;
  padding-horizontal: 10px;
  border-radius: 10px;
  margin-right: 6px;
  background-color: ${({theme, isActive}) =>
    isActive ? theme.primary + '22' : 'transparent'};
  white-space: nowrap;
`;

const Content = styled(ScrollView)`
  flex: 1;
  min-height: 0;
`;

const ContentWrapper = styled.View`
  padding: 10px;
`;

const Footer = styled.View`
  padding: 10px 12px;
  flex-direction: row;
  justify-content: flex-end;
  border-top-width: 1px;
  border-top-color: ${({theme}) => theme.border}22;
  flex-shrink: 0;
`;

const ActionButton = styled.Pressable`
  padding: 8px 12px;
  border-radius: 8px;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const JsonText = styled(TextGeneric)`
  font-family: monospace;
  font-size: ${({theme}) => theme.text.s9}px;
  color: ${({theme}) => theme.text.dark};
`;

const DebugModal: React.FC<DebugModalProps> = ({isOpen, onClose, data}) => {
  const theme = useTheme();
  const {wdp, hdp, isMobile} = useResponsiveDimensions();
  const [activeTab, setActiveTab] = useState(0);

  const tabs = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return [{name: 'Debug', type: 'json', content: {}}];
    }
    return data.map((item, index) => ({
      name: item?.name || `Tab ${index + 1}`,
      type: item?.type || (typeof item?.content === 'string' ? 'text' : 'json'),
      content: item?.content ?? item,
    }));
  }, [data]);

  const currentTab = tabs[activeTab] || tabs[0];

  const handleCopy = () => {
    const content = currentTab?.content ?? {};
    const textToCopy =
      typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    copyToClipboard(textToCopy);
  };

  const content = (
    <>
      <Header>
        <TextGeneric bold style={{color: theme.text.dark}}>
          Debug Information
        </TextGeneric>
        <ActionButton onPress={onClose}>
          <Icon name="close" size={theme.text.s6} color={theme.text.dark + 'AA'} />
        </ActionButton>
      </Header>
      <Tabs
        horizontal
        showsHorizontalScrollIndicator={true}
        contentContainerStyle={{
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        {tabs.map((tab, index) => (
          <TabButton
            key={`${tab.name}-${index}`}
            isActive={index === activeTab}
            onPress={() => setActiveTab(index)}
          >
            <TextGeneric
              style={{
                color: index === activeTab ? theme.primary : theme.text.grey,
              }}
            >
              {tab.name}
            </TextGeneric>
          </TabButton>
        ))}
      </Tabs>
      <Content>
        <ContentWrapper>
          {currentTab?.type === 'text' ? (
            <Markdown
              markdownit={markdownItInstance}
              style={{
                ...markdownStyle(theme),
                body: {
                  ...markdownStyle(theme).body,
                  color: theme.text.dark,
                },
              }}
              rules={markdownRules(theme)}>
              {currentTab.content || ''}
            </Markdown>
          ) : (
            <JsonText>
              {JSON.stringify(currentTab?.content || {}, null, 2)}
            </JsonText>
          )}
        </ContentWrapper>
      </Content>
      <Footer>
        <ActionButton onPress={handleCopy}>
          <Icon name="copyMessage" size={theme.text.s6} color={theme.text.dark} />
          <TextGeneric style={{color: theme.text.dark}}>Copy</TextGeneric>
        </ActionButton>
      </Footer>
    </>
  );

  if (isWeb && !isMobile) {
    if (!isOpen) return null;
    const modalWidth =
      typeof window !== 'undefined'
        ? Math.min(window.innerWidth * 0.9, 900)
        : 900;
    const modalHeight =
      typeof window !== 'undefined'
        ? Math.min(window.innerHeight * 0.85, 700)
        : 700;
    return (
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1999,
        }}
      >
        <View
          style={{
            width: modalWidth,
            height: modalHeight,
            backgroundColor: theme.screenBackground,
            borderRadius: 8,
            overflow: 'hidden',
            shadowColor: '#000',
            shadowOpacity: 0.58,
            shadowRadius: 16,
            elevation: 24,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {content}
        </View>
        <TouchableOpacity
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
          }}
          onPress={onClose}
          activeOpacity={1}
        />
      </View>
    );
  }

  return (
    <Modal visible={isOpen} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}>
        <TouchableOpacity
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
          activeOpacity={1}
          onPress={onClose}
        />
        <View
          style={{
            position: 'absolute',
            top: hdp(12),
            left: 16,
            right: 16,
            height: hdp(76),
            backgroundColor: theme.homeBackground || theme.screenBackground,
            borderRadius: 10,
            overflow: 'hidden',
          }}>
          {content}
        </View>
      </View>
    </Modal>
  );
};

export default DebugModal;
